import z from 'zod';
import { defineMultiTeamQuery } from '../shared/procedures';

/**
 * Escapes LIKE wildcards so a search term containing %, _ or \ matches
 * literally — the same escaping the tRPC roster route applies
 * (roster/utils.ts, athleteTeamProfilePredicate).
 */
function escapeLikePattern(value: string) {
  return value.replace(/[\\%_]/g, '\\$&');
}

/**
 * The athlete roster's Zero read path (SC-5705). Mirrors the eligibility
 * semantics the tRPC `roster.teamAthletes` route and the permissions coach
 * list share through `athleteTeamProfilePredicate`: active profiles with an
 * athlete-type ProfileTeamMembership row on one of the requested teams,
 * joined to their AthleteTeamAttributes row.
 *
 * `teamAthletes` (the dual-path hook's list read) and `athleteById`
 * (permalink support) root on ProfileTeamMembership — the attributes join is
 * (orgId, profileId, teamId)-keyed, so membership rows carry it as a
 * relationship while profile rows would not. One row per (profile, team)
 * membership, exactly like the tRPC route's join.
 *
 * `athletesPage` + `athleteProfileById` (SC-5674) root on Profile — the paged
 * reads the zero-virtual virtualizer drives need to page and search on
 * profile columns (family-name order, given OR family name search), which
 * only a Profile root can express. Each row carries the athlete's
 * memberships on the requested teams, each with its team's
 * AthleteTeamAttributes. The list query sorts deterministically on
 * root-table columns so cursor-paged reads stay consistent across scroll
 * direction.
 */
export const rosterQueries = {
  teamAthletes: defineMultiTeamQuery(
    z.object({ teamIds: z.array(z.uuid()).min(1) }),
    ({ args: { teamIds }, orgQuery }) =>
      orgQuery('ProfileTeamMembership')
        .where('teamId', 'IN', teamIds)
        .where('type', '=', 'athlete')
        .whereExists('profile', (q) => q.where('isActive', '=', true))
        // The tRPC route inner-joins the attributes row, so athletes
        // without one never appear; `.related` is a left join, so the
        // same exclusion has to be spelled out here.
        .whereExists('teamMembershipAttributes')
        .related('profile')
        .related('teamMembershipAttributes')
        .orderBy('createdAt', 'asc')
        .orderBy('id', 'asc'),
  ),
  /**
   * A single membership row (with its related rows) by id — permalink
   * support. Multi-team validated like the list read: without the teamIds
   * check any org member could read any membership row, whatever their
   * team access.
   */
  athleteById: defineMultiTeamQuery(
    z.object({ id: z.uuid(), teamIds: z.array(z.uuid()).min(1) }),
    ({ args: { id, teamIds }, orgQuery }) =>
      orgQuery('ProfileTeamMembership')
        .where('id', '=', id)
        .where('teamId', 'IN', teamIds)
        .where('type', '=', 'athlete')
        .whereExists('profile', (q) => q.where('isActive', '=', true))
        .whereExists('teamMembershipAttributes')
        .related('profile')
        .related('teamMembershipAttributes')
        .one(),
  ),
  /**
   * A single roster profile by profile id (the virtualizer's permalink /
   * getSingleQuery lookup), carrying the same membership nesting. Distinct
   * from `athleteById`, which keys a ProfileTeamMembership row.
   */
  athleteProfileById: defineMultiTeamQuery(
    z.object({ id: z.uuid(), teamIds: z.array(z.uuid()).min(1) }),
    ({ args: { id, teamIds }, orgQuery }) =>
      orgQuery('Profile')
        .where('id', '=', id)
        .where('isActive', '=', true)
        .whereExists('athleteMemberships', (m) =>
          m
            .where('teamId', 'IN', teamIds)
            .where('type', '=', 'athlete')
            .whereExists('teamMembershipAttributes'),
        )
        .related('athleteMemberships', (m) =>
          m
            .where('teamId', 'IN', teamIds)
            .where('type', '=', 'athlete')
            .whereExists('teamMembershipAttributes')
            .related('teamMembershipAttributes'),
        )
        .one(),
  ),
  /**
   * A page of the roster for the zero-virtual virtualizer: `limit` profiles
   * starting (exclusively) at the `start` cursor, in `dir` order. Filters
   * mirror the track form's picker:
   * - searchTerm — given OR family name contains (ILIKE), like the tRPC route
   * - parentTrackId — athletes rostered on that track (sub-track pickers)
   * - athleteIds + athleteIdsMode — the picker's selected/unselected tabs
   *
   * The sort is deterministic (family name, given name, id) so bidirectional
   * paging stays consistent across scroll direction.
   */
  athletesPage: defineMultiTeamQuery(
    z.object({
      teamIds: z.array(z.uuid()).min(1),
      limit: z.number().int().positive(),
      start: z
        .object({
          familyName: z.string(),
          givenName: z.string(),
          id: z.string(),
        })
        .nullable(),
      dir: z.enum(['forward', 'backward']),
      searchTerm: z.string().optional(),
      parentTrackId: z.uuid().optional(),
      athleteIds: z.array(z.uuid()).optional(),
      athleteIdsMode: z.enum(['in', 'notIn']).optional(),
    }),
    ({ args, orgQuery }) => {
      const ascending = args.dir === 'forward';
      let q = orgQuery('Profile')
        .where('isActive', '=', true)
        .whereExists('athleteMemberships', (m) =>
          m
            .where('teamId', 'IN', args.teamIds)
            .where('type', '=', 'athlete')
            // The tRPC route inner-joins the attributes row; `.related` is
            // a left join, so spell the exclusion out here too.
            .whereExists('teamMembershipAttributes'),
        )
        .related('athleteMemberships', (m) =>
          m
            .where('teamId', 'IN', args.teamIds)
            .where('type', '=', 'athlete')
            .whereExists('teamMembershipAttributes')
            .related('teamMembershipAttributes'),
        )
        .limit(args.limit)
        .orderBy('familyName', ascending ? 'asc' : 'desc')
        .orderBy('givenName', ascending ? 'asc' : 'desc')
        .orderBy('id', ascending ? 'asc' : 'desc');

      if (args.start) {
        q = q.start(args.start, { inclusive: false });
      }
      if (args.parentTrackId) {
        q = q.whereExists('trackAthletes', (t) =>
          t
            .where('trackId', '=', args.parentTrackId)
            .where('deletedAt', 'IS', null),
        );
      }
      if (args.athleteIds) {
        q = q.where(
          'id',
          args.athleteIdsMode === 'notIn' ? 'NOT IN' : 'IN',
          args.athleteIds,
        );
      }
      if (args.searchTerm) {
        const term = `%${escapeLikePattern(args.searchTerm)}%`;
        q = q.where(({ or, cmp }) =>
          or(cmp('familyName', 'ILIKE', term), cmp('givenName', 'ILIKE', term)),
        );
      }
      return q;
    },
  ),
};
